import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Button, Card, CardContent, CardMedia, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, Switch, TextField, Typography, Snackbar, Alert,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from "@mui/material";
import { Add, Edit, Delete, Refresh } from "@mui/icons-material";
import { api_urls } from "../../utils/api-urls/index.js";

const API = api_urls + "api/grow";

const GrowCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [form, setForm] = useState({ name: "", nameHi: "", description: "", icon: "", sortOrder: 0 });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/categories`);
      if (data.success) setCategories(data.data);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to fetch categories", severity: "error" });
    }
    setLoading(false);
  };

  useEffect(() => { fetchCategories(); }, []);

  const openAdd = () => {
    setEditingCat(null);
    setForm({ name: "", nameHi: "", description: "", icon: "", sortOrder: 0 });
    setImageFile(null);
    setImagePreview("");
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditingCat(cat);
    setForm({ name: cat.name, nameHi: cat.nameHi || "", description: cat.description || "", icon: cat.icon || "", sortOrder: cat.sortOrder || 0 });
    setImageFile(null);
    setImagePreview(cat.image ? api_urls + cat.image : "");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("nameHi", form.nameHi);
    fd.append("description", form.description);
    fd.append("icon", form.icon);
    fd.append("sortOrder", form.sortOrder);
    if (imageFile) fd.append("image", imageFile);

    try {
      if (editingCat) {
        await axios.put(`${API}/categories/${editingCat._id}`, fd);
        setSnackbar({ open: true, message: "Category updated!", severity: "success" });
      } else {
        await axios.post(`${API}/categories`, fd);
        setSnackbar({ open: true, message: "Category created!", severity: "success" });
      }
      setDialogOpen(false);
      fetchCategories();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || "Error saving", severity: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category and all its services?")) return;
    try {
      await axios.delete(`${API}/categories/${id}`);
      setSnackbar({ open: true, message: "Category deleted", severity: "success" });
      fetchCategories();
    } catch (err) {
      setSnackbar({ open: true, message: "Error deleting", severity: "error" });
    }
  };

  const handleToggle = async (id) => {
    try {
      await axios.put(`${API}/categories/${id}/toggle`);
      fetchCategories();
    } catch (err) {
      setSnackbar({ open: true, message: "Error toggling status", severity: "error" });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">🌱 Grow - Categories</Typography>
        <Box>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchCategories} sx={{ mr: 1 }}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd} color="success">Add Category</Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f5f5f5" }}>
              <TableCell>Image</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Name (Hindi)</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">No categories yet. Add one!</TableCell></TableRow>
            ) : categories.map((cat) => (
              <TableRow key={cat._id}>
                <TableCell>
                  {cat.image ? <img src={api_urls + cat.image} alt={cat.name} style={{ width: 50, height: 50, borderRadius: 8, objectFit: "cover" }} /> : "-"}
                </TableCell>
                <TableCell><strong>{cat.name}</strong></TableCell>
                <TableCell>{cat.nameHi || "-"}</TableCell>
                <TableCell>{cat.description ? cat.description.substring(0, 50) + "..." : "-"}</TableCell>
                <TableCell>{cat.sortOrder}</TableCell>
                <TableCell>
                  <Switch checked={cat.isActive} onChange={() => handleToggle(cat._id)} color="success" />
                  <Chip label={cat.isActive ? "Active" : "Inactive"} color={cat.isActive ? "success" : "default"} size="small" />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => openEdit(cat)} color="primary"><Edit /></IconButton>
                  <IconButton onClick={() => handleDelete(cat._id)} color="error"><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCat ? "Edit Category" : "Add Category"}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Category Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ mt: 2, mb: 2 }} />
          <TextField fullWidth label="Category Name (Hindi)" value={form.nameHi} onChange={(e) => setForm({ ...form, nameHi: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} multiline rows={2} sx={{ mb: 2 }} />
          <TextField fullWidth label="Icon (MaterialCommunityIcons name)" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth label="Sort Order" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} sx={{ mb: 2 }} />
          <Button variant="outlined" component="label" fullWidth>
            {imageFile ? "Change Image" : "Upload Image"}
            <input type="file" hidden accept="image/*" onChange={handleImageChange} />
          </Button>
          {imagePreview && <Box mt={1}><img src={imagePreview} alt="preview" style={{ width: 100, height: 100, objectFit: "cover", borderRadius: 8 }} /></Box>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name} color="success">Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default GrowCategories;
